
import { useState, useEffect } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export interface AuthState {
  session: Session | null;
  user: User | null;
  profile: any | null;
  loading: boolean;
}

export const useAuthState = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const setupAuth = async () => {
      try {
        console.log('Setting up auth state...');
        
        // Set up auth state listener FIRST
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          (event, currentSession) => {
            console.log("Auth state change event:", event, currentSession?.user?.id);
            
            if (event === 'SIGNED_OUT' || !currentSession) {
              // Clear all auth state on sign out
              console.log('Clearing auth state due to sign out');
              setSession(null);
              setUser(null);
              setProfile(null);
            } else {
              // Update session and user for other events
              console.log('Updating auth state with new session');
              setSession(currentSession);
              setUser(currentSession?.user ?? null);
              
              // Fetch profile if user is available
              if (currentSession?.user) {
                setTimeout(() => {
                  fetchProfile(currentSession.user.id);
                }, 0);
              }
            }
          }
        );

        // THEN check for existing session
        console.log('Checking for existing session...');
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting session:', error);
          // If there's an error getting the session, clear any stale data
          setSession(null);
          setUser(null);
          setProfile(null);
        } else {
          console.log('Current session:', currentSession?.user?.id);
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
          
          if (currentSession?.user) {
            await fetchProfile(currentSession.user.id);
          }
        }
        
        setLoading(false);
        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error('Auth setup error:', error);
        setLoading(false);
      }
    };
    
    setupAuth();
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      console.log('Fetching profile for user:', userId);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
        
      if (error) {
        console.error('Error fetching profile:', error);
        return;
      }
      
      console.log('Profile fetched successfully');
      setProfile(data);
    } catch (error) {
      console.error('Profile fetch error:', error);
    }
  };

  return {
    session,
    user,
    profile,
    loading,
    setProfile,
    setSession,
    setUser
  };
};
